import { useProctoredAttempt } from '../components/focus-guard/FocusGuardContext';
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MainLayout } from '../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';

// Import stage components from sourcing folder
import JobBriefingStage from '../components/sourcing/JobBriefingStage';
import SearchStrategyStage from '../components/sourcing/SearchStrategyStage';
import CandidateReviewStage from '../components/sourcing/CandidateReviewStage';
import AiDiscussionStage from '../components/sourcing/AiDiscussionStage';
import ResultsStage from '../components/sourcing/ResultsStage';
import { STAGES, difficultyColors } from '../components/sourcing/constants';

import {
    ArrowLeft,
    Search,
    Users,
    Briefcase,
    Building2,
    MapPin,
    Clock,
    CheckCircle,
    Loader2,
    MessageSquare,
    Award,
    FileText,
    Lightbulb
} from 'lucide-react';

// Stage info for progress display
const STAGE_INFO = {
    [STAGES.JOB_BRIEFING]: { 
        label: 'Job Analysis', 
        icon: FileText, 
        description: 'Understand the job requirements deeply',
        color: 'text-blue-500'
    },
    [STAGES.SEARCH_STRATEGY]: { 
        label: 'Search String', 
        icon: Search, 
        description: 'Build your Boolean search query',
        color: 'text-amber-500'
    },
    [STAGES.CANDIDATE_REVIEW]: { 
        label: 'Candidate Review', 
        icon: Users, 
        description: 'Evaluate candidates for fit',
        color: 'text-emerald-500'
    },
    [STAGES.AI_DISCUSSION]: { 
        label: 'AI Discussion', 
        icon: MessageSquare, 
        description: 'Discuss your decisions with AI mentor',
        color: 'text-purple-500'
    },
    [STAGES.COMPLETED]: { 
        label: 'Results', 
        icon: Award, 
        description: 'View your performance',
        color: 'text-primary'
    }
};

const SourcingLabSession = () => {
    const { sessionId } = useParams();
    const assignedTask = useSearchParams()[0].get('qt');
    const navigate = useNavigate();
    const { api } = useAuth();
    const messagesEndRef = useRef(null);
    
    const [session, setSession] = useState(null);
    useProctoredAttempt({ active: Boolean(session) && session.status !== STAGES.COMPLETED, label: 'sourcing lab', onForceEnd: () => completeSession() });
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    
    // Search string state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchReasoning, setSearchReasoning] = useState('');
    const [selectedPlatforms, setSelectedPlatforms] = useState(['LinkedIn']);
    const [searchEvaluation, setSearchEvaluation] = useState(null);
    
    // Candidate review state
    const [currentCandidateIndex, setCurrentCandidateIndex] = useState(0);
    const [candidateVerdicts, setCandidateVerdicts] = useState({});
    const [candidateReasonings, setCandidateReasonings] = useState({});
    const [evaluationProgress, setEvaluationProgress] = useState(null);
    
    // AI Discussion state
    const [aiMessages, setAiMessages] = useState([]);
    const [aiInput, setAiInput] = useState('');
    const [aiLoading, setAiLoading] = useState(false);

    useEffect(() => {
        fetchSession();
    }, [sessionId]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [aiMessages]);

    const fetchSession = async () => {
        try {
            const response = await api.get(`/sourcing-labs/sessions/${sessionId}`);
            setSession(response.data);
            
            // Load existing AI messages
            if (response.data.ai_messages) {
                setAiMessages(response.data.ai_messages);
            }
            
            // Load existing evaluations
            if (response.data.evaluations) {
                const verdicts = {};
                const reasonings = {};
                Object.entries(response.data.evaluations).forEach(([candId, evalData]) => {
                    verdicts[candId] = evalData.fit_status;
                    reasonings[candId] = evalData.reasoning;
                });
                setCandidateVerdicts(verdicts);
                setCandidateReasonings(reasonings);
            }
            
            // Load search string if exists
            if (response.data.selected_search_string) {
                setSearchQuery(response.data.selected_search_string.query || '');
                setSearchReasoning(response.data.selected_search_string.reasoning || '');
                setSelectedPlatforms(response.data.selected_search_string.platforms || ['LinkedIn']);
                setSearchEvaluation(response.data.selected_search_string.evaluation);
            }
        } catch (error) {
            console.error('Failed to load session:', error);
            const errorMsg = error.response?.data?.detail || 'Failed to load session';
            toast.error(errorMsg, {
                description: 'Please try starting a new session.',
                duration: 5000
            });
            // Add small delay before navigation to let user see the error
            setTimeout(() => {
                navigate('/sourcing-labs');
            }, 1500);
        } finally {
            setLoading(false);
        }
    };

    const advanceStage = async () => {
        setActionLoading(true);
        try {
            await api.post(`/sourcing-labs/sessions/${sessionId}/advance`);
            await fetchSession();
            toast.success('Moving to next stage');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to advance');
        } finally {
            setActionLoading(false);
        }
    };

    const submitSearchString = async () => {
        if (!searchQuery.trim()) {
            toast.error('Please enter a search string');
            return;
        }
        
        setActionLoading(true);
        try {
            const response = await api.post(`/sourcing-labs/sessions/${sessionId}/search-string`, {
                query: searchQuery,
                platforms: selectedPlatforms,
                reasoning: searchReasoning,
                filters_used: {}
            });
            
            setSearchEvaluation(response.data.evaluation);
            toast.success('Search string evaluated!');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit search string');
        } finally {
            setActionLoading(false);
        }
    };

    const submitCandidateEvaluation = async (candidateId) => {
        const verdict = candidateVerdicts[candidateId];
        const reasoning = candidateReasonings[candidateId];
        
        if (!verdict || !reasoning?.trim()) {
            toast.error('Please select a verdict and provide reasoning');
            return;
        }
        
        setActionLoading(true);
        
        // Set a timeout to prevent indefinite loading
        const timeoutId = setTimeout(() => {
            setActionLoading(false);
            toast.error('Evaluation submission timed out. Please try again.');
        }, 30000); // 30 second timeout
        
        try {
            await api.post(`/sourcing-labs/sessions/${sessionId}/evaluate-candidate`, {
                candidate_id: candidateId,
                fit_status: verdict,
                reasoning: reasoning,
                strengths_identified: [],
                concerns_identified: [],
                confidence_level: 3
            }, {
                timeout: 25000 // 25 second request timeout
            });
            
            clearTimeout(timeoutId);
            
            // Update progress
            try {
                const progressRes = await api.get(`/sourcing-labs/sessions/${sessionId}/evaluation-progress`);
                setEvaluationProgress(progressRes.data);
            } catch (progressError) {
                console.error('Failed to fetch progress:', progressError);
                // Don't fail the whole operation if progress fetch fails
            }
            
            toast.success('Evaluation submitted!');
            
            // Move to next candidate
            if (currentCandidateIndex < (session?.candidate_pool?.length || 0) - 1) {
                setCurrentCandidateIndex(prev => prev + 1);
            }
        } catch (error) {
            clearTimeout(timeoutId);
            console.error('Evaluation submission error:', error);
            const errorMessage = error.response?.data?.detail || 
                                 error.message || 
                                 'Failed to submit evaluation. Please try again.';
            toast.error(errorMessage);
        } finally {
            clearTimeout(timeoutId);
            setActionLoading(false);
        }
    };

    const sendAiMessage = async (directMessage = null) => {
        // Use direct message if provided, otherwise use state
        const messageToSend = directMessage || aiInput;
        if (!messageToSend?.trim()) return;
        
        const userMessage = messageToSend.trim();
        setAiInput('');
        setAiLoading(true);
        
        // Optimistic UI update
        setAiMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        
        try {
            const response = await api.post(`/sourcing-labs/sessions/${sessionId}/ai-discuss`, {
                content: userMessage
            });
            
            setAiMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch (error) {
            toast.error('Failed to get AI response');
            // Remove optimistic message on error
            setAiMessages(prev => prev.slice(0, -1));
        } finally {
            setAiLoading(false);
        }
    };

    const completeSession = async () => {
        setActionLoading(true);
        try {
            const response = await api.post(`/sourcing-labs/sessions/${sessionId}/complete`);
            setSession(response.data);
            
            // Show success message
            toast.success('Session completed! View your results.');
            
            // If Phase 6 was auto-updated, show an additional toast
            if (response.data.phase_6_updated) {
                setTimeout(() => {
                    toast.success(response.data.phase_6_message || 'R-PAT Phase 6 progress updated!', {
                        duration: 6000,
                        action: response.data.phase_6_message?.includes('Phase 7') ? {
                            label: 'Go to Phase 7',
                            onClick: () => navigate('/rpat/certification/roleplay')
                        } : undefined
                    });
                }, 1000);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to complete session');
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) {
        return (
            <MainLayout>
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-12 w-12 animate-spin text-primary" />
                </div>
            </MainLayout>
        );
    }

    if (!session) {
        return (
            <MainLayout>
                <div className="text-center py-12">
                    <p className="text-muted-foreground">Session not found</p>
                    <Button onClick={() => navigate('/sourcing-labs')} className="mt-4">
                        Back to Labs
                    </Button>
                </div>
            </MainLayout>
        );
    }

    const currentStage = session.status;
    const jobBrief = session.job_brief;
    const candidates = session.candidate_pool || [];
    const currentCandidate = candidates[currentCandidateIndex];
    
    const stageOrder = [STAGES.JOB_BRIEFING, STAGES.SEARCH_STRATEGY, STAGES.CANDIDATE_REVIEW, STAGES.AI_DISCUSSION, STAGES.COMPLETED];
    const currentStageIndex = stageOrder.indexOf(currentStage);

    return (
        <MainLayout>
            <div className="space-y-6 animate-fade-in" data-testid="sourcing-session-page">
                {/* Header */}
                <div className="flex items-start gap-4">
                    {!assignedTask && (
                        <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => navigate('/sourcing-labs')}
                            data-testid="back-btn"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    )}
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                            <h1 className="text-2xl font-bold">Sourcing Lab Session</h1>
                            <Badge variant="outline" className={difficultyColors[session.difficulty]}>
                                {session.difficulty}
                            </Badge>
                        </div>
                        <p className="text-muted-foreground">
                            {jobBrief?.title} • {jobBrief?.industry}
                        </p>
                    </div>
                </div>

                {/* Stage Progress */}
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            {stageOrder.map((stage, idx) => {
                                const StageIcon = STAGE_INFO[stage].icon;
                                const isComplete = idx < currentStageIndex;
                                const isCurrent = idx === currentStageIndex;
                                
                                return (
                                    <div key={stage} className="flex items-center">
                                        <div className={`flex flex-col items-center ${idx > 0 ? 'ml-4' : ''}`}>
                                            <div className={`
                                                w-10 h-10 rounded-full flex items-center justify-center transition-all
                                                ${isComplete ? 'bg-emerald-500 text-white' : 
                                                  isCurrent ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' : 
                                                  'bg-muted text-muted-foreground'}
                                            `}>
                                                {isComplete ? (
                                                    <CheckCircle className="h-5 w-5" />
                                                ) : (
                                                    <StageIcon className="h-5 w-5" />
                                                )}
                                            </div>
                                            <span className={`text-xs mt-1 font-medium ${isCurrent ? 'text-primary' : 'text-muted-foreground'}`}>
                                                {STAGE_INFO[stage].label}
                                            </span>
                                        </div>
                                        {idx < stageOrder.length - 1 && (
                                            <div className={`h-0.5 w-12 mx-2 ${idx < currentStageIndex ? 'bg-emerald-500' : 'bg-muted'}`} />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>

                {/* Stage Content */}
                <div className="grid gap-6 lg:grid-cols-3">
                    {/* Main Content Area */}
                    <div className="lg:col-span-2 space-y-6">
                        {/* Stage 1: Job Briefing */}
                        {currentStage === STAGES.JOB_BRIEFING && (
                            <JobBriefingStage 
                                jobBrief={jobBrief} 
                                onContinue={advanceStage}
                                loading={actionLoading}
                            />
                        )}

                        {/* Stage 2: Search Strategy */}
                        {currentStage === STAGES.SEARCH_STRATEGY && (
                            <SearchStrategyStage
                                jobBrief={jobBrief}
                                searchQuery={searchQuery}
                                setSearchQuery={setSearchQuery}
                                searchReasoning={searchReasoning}
                                setSearchReasoning={setSearchReasoning}
                                selectedPlatforms={selectedPlatforms}
                                setSelectedPlatforms={setSelectedPlatforms}
                                searchEvaluation={searchEvaluation}
                                onSubmit={submitSearchString}
                                onContinue={advanceStage}
                                loading={actionLoading}
                                api={api}
                            />
                        )}

                        {/* Stage 3: Candidate Review */}
                        {currentStage === STAGES.CANDIDATE_REVIEW && (
                            <CandidateReviewStage
                                candidates={candidates}
                                currentIndex={currentCandidateIndex}
                                setCurrentIndex={setCurrentCandidateIndex}
                                currentCandidate={currentCandidate}
                                verdicts={candidateVerdicts}
                                setVerdicts={setCandidateVerdicts}
                                reasonings={candidateReasonings}
                                setReasonings={setCandidateReasonings}
                                onSubmitEvaluation={submitCandidateEvaluation}
                                evaluationProgress={evaluationProgress}
                                onContinue={advanceStage}
                                loading={actionLoading}
                                jobBrief={jobBrief}
                                api={api}
                                sessionId={session?.id}
                            />
                        )}

                        {/* Stage 4: AI Discussion */}
                        {currentStage === STAGES.AI_DISCUSSION && (
                            <AiDiscussionStage
                                messages={aiMessages}
                                input={aiInput}
                                setInput={setAiInput}
                                onSend={sendAiMessage}
                                aiLoading={aiLoading}
                                messagesEndRef={messagesEndRef}
                                onComplete={completeSession}
                                loading={actionLoading}
                                api={api}
                                session={session}
                            />
                        )}

                        {/* Stage 5: Results */}
                        {currentStage === STAGES.COMPLETED && (
                            <ResultsStage session={session} />
                        )}
                    </div>

                    {/* Sidebar - Job Context */}
                    <div className="space-y-4">
                        <Card className="sticky top-4">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Briefcase className="h-4 w-4 text-primary" />
                                    Job Quick Reference
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="text-sm space-y-3">
                                <div>
                                    <p className="font-medium">{jobBrief?.title}</p>
                                    <p className="text-muted-foreground text-xs">{jobBrief?.company_type}</p>
                                </div>
                                <Separator />
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Building2 className="h-3 w-3" />
                                    {jobBrief?.industry}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <Clock className="h-3 w-3" />
                                    {jobBrief?.experience_range?.min}-{jobBrief?.experience_range?.max} years
                                </div>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3" />
                                    {jobBrief?.location} ({jobBrief?.work_mode})
                                </div>
                                <Separator />
                                <div>
                                    <p className="font-medium text-xs mb-2">Key Skills</p>
                                    <div className="flex flex-wrap gap-1">
                                        {jobBrief?.required_skills?.slice(0, 5).map((skill, idx) => (
                                            <Badge key={idx} variant="secondary" className="text-xs">
                                                {skill}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Tips Card */}
                        {currentStage !== STAGES.COMPLETED && (
                        <Card className="border-amber-500/30 bg-amber-500/5" data-testid="sourcing-tips-card">
                            <CardHeader className="pb-2">
                                <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
                                    <Lightbulb className="h-4 w-4" />
                                    Tips
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="text-xs text-muted-foreground">
                                {currentStage === STAGES.JOB_BRIEFING && (
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li>Note the ideal background companies/industries</li>
                                        <li>Understand what makes someone NOT a fit</li>
                                        <li>Pay attention to deal breakers</li>
                                    </ul>
                                )}
                                {currentStage === STAGES.SEARCH_STRATEGY && (
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li>Use Boolean operators (AND, OR, NOT)</li>
                                        <li>Include company types and industries</li>
                                        <li>Add experience level filters</li>
                                    </ul>
                                )}
                                {currentStage === STAGES.CANDIDATE_REVIEW && (
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li>Check industry and company background</li>
                                        <li>Look for career progression patterns</li>
                                        <li>Consider job stability indicators</li>
                                    </ul>
                                )}
                                {currentStage === STAGES.AI_DISCUSSION && (
                                    <ul className="space-y-1 list-disc list-inside">
                                        <li>Ask about specific candidates</li>
                                        <li>Discuss your reasoning</li>
                                        <li>Get feedback on what you missed</li>
                                    </ul>
                                )}
                            </CardContent>
                        </Card>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default SourcingLabSession;
